import { useState } from 'react'
import { colors, fonts, sentiment } from './theme'
import { INSTITUTIONS, MARKET_DATA, LEVERAGE_STACK, CLO_TRANCHES } from './constants'
import MetricCard from './components/MetricCard'
import SectionBar from './components/SectionBar'

export default function MarketOverview() {
  const [selectedView, setSelectedView] = useState('dashboard')

  const subTabs = [
    { key: 'dashboard', label: 'Dashboard' },
    { key: 'clo_ecosystem', label: 'CLO Ecosystem' },
    { key: 'pe_nexus', label: 'PE-Credit Nexus' },
    { key: 'leverage_stack', label: 'Leverage Stack' },
  ]

  return (
    <div style={styles.page}>
      <div style={styles.subNav}>
        {subTabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setSelectedView(tab.key)}
            style={{
              ...styles.subTab,
              ...(selectedView === tab.key ? styles.subTabActive : {}),
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {selectedView === 'dashboard' && <DashboardView />}
      {selectedView === 'clo_ecosystem' && <CLOEcosystemView />}
      {selectedView === 'pe_nexus' && <PECreditNexusView />}
      {selectedView === 'leverage_stack' && <LeverageStackView />}
    </div>
  )
}

function DashboardView() {
  const institutions = Object.values(INSTITUTIONS)

  return (
    <div style={styles.content}>
      <SectionBar title="Private Credit Market Pulse" subtitle="Real-time market overview" accent="gold" />

      <div style={styles.metricsRow}>
        <MetricCard label="Total Private Credit AUM" value={MARKET_DATA.totalAUM} sublabel="Global" sentiment="accent" />
        <MetricCard label="Projected 2028 AUM" value={MARKET_DATA.projectedAUM2028} sublabel="65% growth" sentiment="positive" />
        <MetricCard label="CLO Market" value={MARKET_DATA.cloMarketSize} sublabel="Outstanding" />
        <MetricCard label="Leveraged Loan Market" value={MARKET_DATA.leveragedLoanMarket} />
        <MetricCard label="BDC Market Cap" value={MARKET_DATA.bdcMarketCap} sublabel="Public BDCs" />
      </div>

      <SectionBar title="Institution Tracker" subtitle="Key private credit players" />

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Ticker</th>
              <th style={styles.th}>Institution</th>
              <th style={styles.th}>Type</th>
              <th style={styles.th}>Focus</th>
            </tr>
          </thead>
          <tbody>
            {institutions.map(inst => (
              <tr key={inst.ticker} style={styles.tr}>
                <td style={{ ...styles.td, ...styles.ticker }}>{inst.ticker}</td>
                <td style={styles.td}>{inst.name}</td>
                <td style={{ ...styles.td, color: colors.textMuted }}>{inst.type}</td>
                <td style={{ ...styles.td, fontStyle: 'italic', color: colors.textSecondary }}>{inst.focus}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SectionBar title="Key Structural Facts" subtitle="Why private credit matters" accent="gold" />

      <div style={styles.factsGrid}>
        <FactCard
          title="CLO Dominance"
          value="70%"
          detail="of all leveraged loans are purchased by CLOs — they ARE the market"
          sentiment="featured"
        />
        <FactCard
          title="PE Supply"
          value="~60%"
          detail="of new leveraged loan supply comes from PE buyouts"
          sentiment="featured"
        />
        <FactCard
          title="AAA Track Record"
          value="0%"
          detail="realized loss rate on CLO AAA tranches historically — even through 2008"
          sentiment="positive"
        />
        <FactCard
          title="Leverage Amplification"
          value="500x"
          detail="cumulative leverage from company EBITDA through CLO to AAA repo"
          sentiment="negative"
        />
      </div>
    </div>
  )
}

function CLOEcosystemView() {
  return (
    <div style={styles.content}>
      <SectionBar title="CLO Capital Structure" subtitle="How leveraged loans get securitized" accent="gold" />

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Tranche</th>
              <th style={styles.th}>Rating</th>
              <th style={styles.th}>Typical Spread</th>
              <th style={styles.th}>Subordination</th>
            </tr>
          </thead>
          <tbody>
            {CLO_TRANCHES.map(t => (
              <tr key={t.tranche} style={styles.tr}>
                <td style={{ ...styles.td, ...styles.ticker }}>{t.tranche}</td>
                <td style={{ ...styles.td, color: colors.textMuted }}>{t.rating}</td>
                <td style={{ ...styles.td, fontFamily: fonts.mono }}>{t.typical_spread}</td>
                <td style={{ ...styles.td, fontFamily: fonts.mono }}>{t.subordination}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SectionBar title="How a CLO Works" subtitle="Step-by-step mechanics" />

      <div style={styles.mechanicsFlow}>
        <FlowStep num={1} title="Loan Origination" desc="PE firm acquires company using leveraged buyout. The acquisition debt becomes a leveraged loan (typically first lien, floating rate, SOFR + 300-500bp)." />
        <FlowStep num={2} title="CLO Formation" desc="CLO manager raises capital by issuing tranched securities (AAA through equity). Uses proceeds to buy 150-250 leveraged loans, creating a diversified portfolio." />
        <FlowStep num={3} title="Waterfall Distribution" desc="Loan interest payments flow through the CLO waterfall: AAA gets paid first (lowest spread, highest safety), then AA, A, BBB, BB. Equity tranche gets the residual." />
        <FlowStep num={4} title="OC & IC Tests" desc="Overcollateralization (OC) and Interest Coverage (IC) tests protect senior tranches. If loan defaults erode the cushion, cash gets diverted from junior to senior tranches." />
        <FlowStep num={5} title="Active Management" desc="CLO manager can trade loans during the reinvestment period (typically 4-5 years). Can sell deteriorating credits and buy better ones — a key advantage over static pools." />
        <FlowStep num={6} title="Refinancing / Reset" desc="After reinvestment period, CLO enters amortization. Manager may refinance (replace expensive tranches with cheaper ones) or reset (extend reinvestment period)." />
      </div>
    </div>
  )
}

function PECreditNexusView() {
  return (
    <div style={styles.content}>
      <SectionBar title="Why PE Can't Exist Without CLOs" subtitle="and vice versa" accent="gold" />

      <div style={styles.nexusGrid}>
        <div style={styles.nexusColumn}>
          <div style={styles.nexusHeader}>PE Needs CLOs Because</div>
          <NexusPoint text="CLOs buy 70% of all leveraged loans — they ARE the market" />
          <NexusPoint text="Without CLO demand, loan spreads would be much wider, making LBOs more expensive and reducing PE returns" />
          <NexusPoint text="CLO demand enables the 'leveraged' in 'leveraged buyout' — PE couldn't lever companies 5-7x without deep loan markets" />
          <NexusPoint text="When CLO issuance drops (like 2008), PE deal activity drops too because financing dries up" />
        </div>

        <div style={styles.nexusDivider} />

        <div style={styles.nexusColumn}>
          <div style={styles.nexusHeader}>CLOs Need PE Because</div>
          <NexusPoint text="PE buyouts create ~60% of new leveraged loan supply" />
          <NexusPoint text="Without PE deal flow, CLO managers wouldn't have enough loans to fill their portfolios" />
          <NexusPoint text="PE-backed companies tend to have higher spreads (more leveraged), which generates the excess spread that drives CLO equity returns" />
          <NexusPoint text="Add-on acquisitions and dividend recaps by PE create ongoing loan supply throughout a CLO's reinvestment period" />
        </div>
      </div>

      <SectionBar title="The Doom Loop Risk" subtitle="Theoretical systemic concern" />

      <div style={styles.warningBox}>
        <div style={styles.warningTitle}>Cascading Loss Scenario</div>
        <p style={styles.warningText}>
          A $1M loss at the company level can cascade through the leverage stack. If enough companies default,
          CLO OC tests fail, tranches get downgraded, repo counterparties increase haircuts or call margin,
          forced selling depresses loan prices further, more OC tests fail — a potential doom loop.
        </p>
        <div style={{ ...styles.warningTitle, color: colors.greenInk, marginTop: 12 }}>Historical Reality</div>
        <p style={styles.warningText}>
          This doom loop has NOT occurred historically — CLO AAA tranches have had a 0% realized loss rate
          even through 2008 — because subordination levels are conservatively set and active management can
          trade out of deteriorating credits. But the theoretical risk is what drives regulatory scrutiny.
        </p>
      </div>
    </div>
  )
}

function LeverageStackView() {
  return (
    <div style={styles.content}>
      <SectionBar title="The Leverage Stack" subtitle="How 5x becomes 500x" accent="gold" />

      <p style={styles.description}>
        Each layer of the system adds leverage on top of the previous layer. Follow a single dollar
        of corporate EBITDA through the stack.
      </p>

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Layer</th>
              <th style={styles.th}>Entity</th>
              <th style={styles.th}>Leverage</th>
              <th style={styles.th}>Cumulative</th>
              <th style={styles.th}>What Happens</th>
            </tr>
          </thead>
          <tbody>
            {LEVERAGE_STACK.map(row => (
              <tr key={row.layer} style={styles.tr}>
                <td style={{ ...styles.td, fontFamily: fonts.mono, color: colors.gold }}>{row.layer}</td>
                <td style={{ ...styles.td, ...styles.ticker }}>{row.entity}</td>
                <td style={{ ...styles.td, fontFamily: fonts.mono }}>{row.leverage}</td>
                <td style={{ ...styles.td, fontFamily: fonts.mono, color: row.cumulative === '500x' ? colors.redInk : colors.amberInk }}>{row.cumulative}</td>
                <td style={{ ...styles.td, color: colors.textSecondary }}>{row.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SectionBar title="Layer-by-Layer Proof" subtitle="Mathematical decomposition" />

      <div style={styles.proofSection}>
        <ProofItem
          title="Layer 1: Company Leverage"
          formula="Debt / EBITDA = 5x"
          explanation="A typical PE buyout levers the target at 5-7x EBITDA. The company pays floating-rate interest (SOFR + 350-500bp) on this debt. If EBITDA drops 20%, interest coverage falls from ~2x to ~1.6x."
        />
        <ProofItem
          title="Layer 2: CLO Leverage"
          formula="CLO Assets / Equity = 10x"
          explanation="A CLO with $500M in loans might have ~$50M in equity. The CLO funds the rest by issuing rated debt tranches (AAA through BB). CLO equity investors get the residual spread after paying tranche holders — typically targeting 12-15% IRR."
        />
        <ProofItem
          title="Layer 3: Repo Leverage on AAA"
          formula="AAA Position / Bank Equity = 10x"
          explanation="Banks fund their AAA CLO holdings using repo (repurchase agreements). A bank might post $30M in equity to control $300M in AAA CLO notes. The repo lender provides 90% financing because AAA CLOs have never defaulted. Combined: 5 x 10 x 10 = 500x."
        />
      </div>
    </div>
  )
}

// Sub-components
function FactCard({ title, value, detail, sentiment: sentType }) {
  const s = sentType === 'positive' ? sentiment.positive
    : sentType === 'negative' ? sentiment.negative
    : sentiment.featured

  return (
    <div style={{ ...styles.factCard, borderColor: s.border, background: s.bg }}>
      <div style={{ ...styles.factTitle, color: s.color }}>{title}</div>
      <div style={{ ...styles.factValue, color: s.color }}>{value}</div>
      <div style={styles.factDetail}>{detail}</div>
    </div>
  )
}

function FlowStep({ num, title, desc }) {
  return (
    <div style={styles.flowStep}>
      <div style={styles.flowNum}>{num}</div>
      <div style={styles.flowContent}>
        <div style={styles.flowTitle}>{title}</div>
        <div style={styles.flowDesc}>{desc}</div>
      </div>
    </div>
  )
}

function NexusPoint({ text }) {
  return (
    <div style={styles.nexusPoint}>
      <span style={styles.nexusBullet}>&bull;</span>
      <span style={styles.nexusText}>{text}</span>
    </div>
  )
}

function ProofItem({ title, formula, explanation }) {
  return (
    <div style={styles.proofItem}>
      <div style={styles.proofTitle}>{title}</div>
      <div style={styles.proofFormula}>{formula}</div>
      <div style={styles.proofExplanation}>{explanation}</div>
    </div>
  )
}

const styles = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
  },
  subNav: {
    display: 'flex',
    gap: 2,
    padding: '8px 32px',
    borderBottom: `1px solid ${colors.rule}`,
    background: colors.navyDeep,
  },
  subTab: {
    padding: '4px 14px',
    fontFamily: fonts.mono,
    fontSize: 11,
    fontWeight: 500,
    color: colors.textMuted,
    background: 'transparent',
    border: '1px solid transparent',
    borderRadius: 2,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  subTabActive: {
    color: colors.gold,
    borderColor: colors.goldBorder,
    background: colors.goldBg,
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '24px 32px',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  metricsRow: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
  },
  description: {
    fontFamily: fonts.serif,
    fontSize: 14,
    color: colors.textSecondary,
    lineHeight: 1.6,
  },
  // Table styles
  tableContainer: {
    overflow: 'auto',
    border: `1px solid ${colors.rule}`,
    borderRadius: 3,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: fonts.sans,
    fontSize: 13,
  },
  th: {
    padding: '10px 16px',
    textAlign: 'left',
    fontFamily: fonts.serif,
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: colors.textMuted,
    background: colors.navyDeep,
    borderBottom: `1px solid ${colors.rule}`,
  },
  tr: {
    borderBottom: `1px solid ${colors.ruleLight}`,
  },
  td: {
    padding: '8px 16px',
    color: colors.textPrimary,
  },
  ticker: {
    fontFamily: fonts.mono,
    fontWeight: 600,
    color: colors.steel,
  },
  // Fact cards
  factsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: 12,
  },
  factCard: {
    padding: 16,
    border: '1px solid',
    borderRadius: 3,
  },
  factTitle: {
    fontFamily: fonts.serif,
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  factValue: {
    fontFamily: fonts.mono,
    fontSize: 28,
    fontWeight: 700,
    marginBottom: 6,
  },
  factDetail: {
    fontFamily: fonts.sans,
    fontSize: 12,
    color: colors.textSecondary,
    lineHeight: 1.5,
  },
  // Flow steps
  mechanicsFlow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  flowStep: {
    display: 'flex',
    gap: 16,
    padding: 16,
    background: colors.navySurface,
    border: `1px solid ${colors.rule}`,
    borderRadius: 3,
  },
  flowNum: {
    fontFamily: fonts.mono,
    fontSize: 20,
    fontWeight: 700,
    color: colors.gold,
    minWidth: 32,
    textAlign: 'center',
  },
  flowContent: {
    flex: 1,
  },
  flowTitle: {
    fontFamily: fonts.serif,
    fontSize: 14,
    fontWeight: 600,
    color: colors.steel,
    marginBottom: 4,
  },
  flowDesc: {
    fontFamily: fonts.sans,
    fontSize: 13,
    color: colors.textSecondary,
    lineHeight: 1.6,
  },
  // Nexus view
  nexusGrid: {
    display: 'flex',
    gap: 24,
    padding: 0,
  },
  nexusColumn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  nexusDivider: {
    width: 1,
    background: colors.rule,
  },
  nexusHeader: {
    fontFamily: fonts.serif,
    fontSize: 15,
    fontWeight: 700,
    color: colors.gold,
    marginBottom: 4,
  },
  nexusPoint: {
    display: 'flex',
    gap: 10,
    padding: '8px 12px',
    background: colors.navySurface,
    borderRadius: 3,
    border: `1px solid ${colors.ruleLight}`,
  },
  nexusBullet: {
    color: colors.steel,
    fontSize: 16,
    lineHeight: 1.4,
  },
  nexusText: {
    fontFamily: fonts.sans,
    fontSize: 13,
    color: colors.textSecondary,
    lineHeight: 1.5,
  },
  // Warning box
  warningBox: {
    padding: 20,
    background: colors.navySurface,
    border: `1px solid ${colors.redBorder}`,
    borderLeft: `3px solid ${colors.redInk}`,
    borderRadius: 3,
  },
  warningTitle: {
    fontFamily: fonts.serif,
    fontSize: 14,
    fontWeight: 600,
    color: colors.redInk,
    marginBottom: 8,
  },
  warningText: {
    fontFamily: fonts.sans,
    fontSize: 13,
    color: colors.textSecondary,
    lineHeight: 1.6,
  },
  // Proof section
  proofSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  proofItem: {
    padding: 16,
    background: colors.navyDeep,
    border: `1px solid ${colors.rule}`,
    borderLeft: `3px solid ${colors.gold}`,
    borderRadius: 3,
  },
  proofTitle: {
    fontFamily: fonts.serif,
    fontSize: 13,
    fontWeight: 600,
    color: colors.steel,
    marginBottom: 6,
  },
  proofFormula: {
    fontFamily: fonts.mono,
    fontSize: 16,
    fontWeight: 600,
    color: colors.gold,
    marginBottom: 8,
    padding: '6px 12px',
    background: colors.goldBg,
    borderRadius: 2,
    display: 'inline-block',
  },
  proofExplanation: {
    fontFamily: fonts.sans,
    fontSize: 13,
    color: colors.textSecondary,
    lineHeight: 1.6,
  },
}
